import { useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';

//hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { addEmoji } from '@/utils/addEmoji';
//utils
import { get } from '@/utils/client';
import { useIsElementFocused } from '@/utils/hooks/utils/useIsElementFocused';

import { CAPTION_TYPE, HASHTAG_LIST_TYPE } from '@/lib/captionsHashtagLists/types';
import PropTypes from 'prop-types';

//Libs
import { SEARCH_MENTIONS } from '@/lib/routes/apiEndpoints';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
//components
import { Caption } from '@/components/composite/Caption';
const EmojiPicker = dynamic(() => import('@/components/base/EmojiPicker'));

export const FacebookCaption = ({
    captionLayoutConfig = { hasBorder: true, hasAvatar: true, label: null, aiCaption: false },
    caption,
    updateCaption,
    mentions,
    updateMentions,
    captionWrapperStyle = 'default',
    footerPortalContainer,
    inputProps, // input props like name,id ...
    rows = 8,
    forcedSocialProfile = null,
    rootId // wrapper element when the eventListener will be added to handle focus style of the caption
}) => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentionsRequest, setMentionsRequest] = useState({ status: 'idle' });
    const { wrapperRef, isFocused, setIsFocused } = useIsElementFocused(rootId);

    const inputRef = useRef(null);

    return (
        <Caption captionWrapperStyle={captionWrapperStyle} className="space-y-4" ref={wrapperRef}>
            <Caption.Content className="items-start">
                {captionLayoutConfig?.hasAvatar ? (
                    <Avatar
                        url={
                            (forcedSocialProfile ?? activeSocialProfile)?.external_api_data
                                ?.avatar ?? null
                        }
                        fallback={
                            (forcedSocialProfile ?? activeSocialProfile)?.slug?.charAt(0) ?? 'U'
                        }
                        profileType={
                            (forcedSocialProfile ?? activeSocialProfile)?.social_network_type
                        }
                        className="mr-5"
                    />
                ) : null}
                <Caption.MentionsWrapper
                    className={clsx('mentions', {
                        'cross-post': rows === 4,
                        '!h-[100px]': rows === 5
                    })}
                    inputRef={inputRef}
                    allowSpaceInQuery
                    onBlur={() => {
                        const initialCursorPosition = inputRef.current.selectionStart;

                        const mentionsBeforeCaret = mentions?.filter(
                            (mention) => mention?.plainTextIndex <= initialCursorPosition
                        );

                        //get additionalCharacters to add to cursor position
                        const additionalCharacters = mentionsBeforeCaret?.reduce((acc, obj) => {
                            return acc + (obj.additionalCharacters + obj?.id?.length);
                        }, 0);

                        const cursorInMention = mentions?.find((mention) => {
                            return (
                                parseInt(mention?.plainTextIndex) <= initialCursorPosition &&
                                initialCursorPosition <=
                                    parseInt(mention?.plainTextIndex) + mention?.display.length
                            );
                        });

                        if (cursorInMention) {
                            const newCursorPosition =
                                parseInt(cursorInMention.plainTextIndex) +
                                cursorInMention.display.length +
                                +additionalCharacters +
                                1; // +1 for the space after the mention
                            setCursorPosition(newCursorPosition);
                        } else {
                            //set new cursor position
                            setCursorPosition(initialCursorPosition + additionalCharacters);
                        }
                    }}
                    onFocus={() => setIsFocused(true)}
                    value={caption}
                    onChange={(event, newValue, newPlainTextValue, mentions) => {
                        // additionalCharacters => markup added by the lib @[][]
                        updateMentions(
                            mentions.map((mention) => {
                                return {
                                    ...mention,
                                    additionalCharacters: 5
                                };
                            })
                        );
                        updateCaption(event.target.value);
                    }}
                    {...inputProps}>
                    <Caption.Mention
                        trigger="@"
                        markup="@[__id__][__display__]"
                        className="mention"
                        appendSpaceOnAdd
                        displayTransform={(id, display) => `${display}`}
                        renderSuggestion={(entry, search, highlightedDisplay, index, focused) => {
                            return (
                                <Caption.MentionSuggestion
                                    focused={focused}
                                    highlightedDisplay={highlightedDisplay}
                                    entry={entry}
                                />
                            );
                        }}
                        data={async (query, callback) => {
                            if (!query) return;
                            try {
                                setMentionsRequest({ status: 'pending' });
                                const { data } = await get(SEARCH_MENTIONS, {
                                    social_network_type: 'facebook',
                                    search: query
                                });

                                const formatedData = data?.data?.map((el) => {
                                    return { id: el?.id, display: el?.name, link: el?.link };
                                });

                                callback(formatedData);
                                setMentionsRequest({ status: 'idle' });
                            } catch (e) {
                                setMentionsRequest({ status: 'error' });
                            }
                        }}
                    />
                </Caption.MentionsWrapper>
                {<Caption.Loading requestStatus={mentionsRequest.status} />}
                {<Caption.MentionsRequestError requestStatus={mentionsRequest.status} />}
                {captionLayoutConfig?.hasBorder ? (
                    <Caption.Border
                        className={clsx({
                            'ring-icoBlue': isFocused,
                            'ring-icoGray-400 dark:ring-icoDarkMode-beton': !isFocused
                        })}
                    />
                ) : null}
            </Caption.Content>
            <Caption.Footer className="!z-10 justify-end">
                <div className="flex divide-x divide-icoGray-300 dark:divide-icoDarkMode-beton">
                    <div className="flex flex-1 justify-end gap-5 pr-5">
                        <Caption.Popover
                            type={HASHTAG_LIST_TYPE}
                            currentCaption={caption}
                            updateCaption={updateCaption}
                            forcedSocialProfile={forcedSocialProfile}
                            portalContainer={footerPortalContainer}
                        />
                        <Caption.Popover
                            type={CAPTION_TYPE}
                            currentCaption={caption}
                            updateCaption={updateCaption}
                            forcedSocialProfile={forcedSocialProfile}
                            portalContainer={footerPortalContainer}
                        />
                    </div>
                    <EmojiPicker
                        placement="bottom-end"
                        offset={[5, 10]}
                        selectEmoji={(emoji) =>
                            addEmoji(
                                emoji,
                                caption,
                                updateCaption,
                                cursorPosition,
                                setCursorPosition
                            )
                        }
                        className="ml-5 text-xl"
                        portalContainer={footerPortalContainer}
                    />
                </div>
            </Caption.Footer>
        </Caption>
    );
};

FacebookCaption.propTypes = {
    captionLayoutConfig: PropTypes.object,
    caption: PropTypes.string,
    updateCaption: PropTypes.func,
    mentions: PropTypes.arrayOf(PropTypes.object),
    updateMentions: PropTypes.func,
    inputProps: PropTypes.object
};
